/* inno-player.js — the ambient player, running on EVERY page.

   It used to synthesise a drone with oscillators, which is what made "continuous across the
   site" achievable: a navigation tears down the audio no matter what, and a sustained pad can
   be rebuilt mid-note without anyone hearing the seam. A real track can't be rebuilt — restart
   it on every page and the visitor hears it rewind to zero — so the POSITION travels alongside
   the play state, and each page picks the track up where the last one left it. Saved on the way
   out (pagehide) as well as periodically, so the splice lands within a frame or two rather than
   wherever the last timeupdate happened to fire.

   Why the audio goes through a GainNode instead of `el.volume`: iOS ignores volume on a media
   element entirely — it is read-only there and always 1 — so the resting level, the fades and
   the duck would all do nothing on an iPhone. Routing through Web Audio gives real control on
   every platform. That requires the element to be CORS-clean, hence crossOrigin below; the
   Shopify CDN serves `access-control-allow-origin: *`, so it is. If the graph can't be built we
   fall back to the element's own volume, which is worse on iOS but still plays.

   Autoplay policy: play() is refused without a user gesture, so a resumed session arms a
   one-shot gesture listener and the next touch anywhere picks it back up. Nothing here ever
   starts sound the visitor did not previously ask for. */
(function () {
  'use strict';

  /* The music. Hosted on the shop's own Files, so it is served from the same CDN as everything
     else and is the merchant's to swap: upload a new file in Shopify admin → Content → Files and
     paste its URL here. More than one entry turns `»` into a real skip and stops the looping. */
  var TRACKS = [
    { name: 'signal 01 — stone',
      src: 'https://cdn.shopify.com/s/files/1/0762/5726/3866/files/STONE.mp3?v=1789310221' }
  ];
  var VOL = 0.55;          // resting level — present, but still a bed under the room
  var FADE = 0.35;         // seconds; the time constant for every level change
  var KEY = 'inno_music';
  /* How stale a stored session may be and still be picked up. A navigation between two pages of
     this site is a second or two. Anything longer means the site was closed and something later
     restored the tab — browsers hand sessionStorage back when you reopen a closed tab — and
     music that starts itself on a visit the visitor never asked for is the thing to avoid. */
  var RESUME_GRACE = 90000;

  var el = null, ctx = null, gain = null, srcNode = null;
  var playing = false, trackIdx = 0, ducked = false;
  var pendingSeek = 0, pauseT = null, saveT = 0, playFailed = null, autoPaused = false;
  var transports = [];      // every visible transport — they all reflect one engine
  var toggles = 0;          // diagnostic: how many times a transport asked us to flip

  function readState() {
    try {
      var raw = sessionStorage.getItem(KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      return (v && typeof v === 'object') ? v : null;
    } catch (e) { return null; }
  }
  function writeState() {
    try {
      sessionStorage.setItem(KEY, JSON.stringify({
        playing: playing,
        track: trackIdx,
        t: (el && isFinite(el.currentTime)) ? +el.currentTime.toFixed(2) : pendingSeek,
        savedAt: Date.now()
      }));
    } catch (e) {}
  }

  /* iOS puts a page on the "ambient" audio session by default, which the hardware ring/silent
     switch mutes. Declaring playback moves it to the media session, the one a video uses.
     iOS 16.4+; below that there is no API for it and the switch still wins. */
  function claimPlayback() {
    try {
      if (navigator.audioSession && navigator.audioSession.type !== 'playback') {
        navigator.audioSession.type = 'playback';
      }
    } catch (e) {}
  }

  function graph() {
    var AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return false;
    try {
      ctx = new AC();
      gain = ctx.createGain();
      gain.gain.value = 0;
      srcNode = ctx.createMediaElementSource(el);
      srcNode.connect(gain);
      gain.connect(ctx.destination);
      return true;
    } catch (e) {
      // a tainted element would output silence rather than throw, so if this fails at all we
      // want the plain element path, not a graph we can't hear
      ctx = null; gain = null; srcNode = null;
      return false;
    }
  }

  function build() {
    if (el) return true;
    if (!document.body) return false;
    el = document.createElement('audio');
    el.loop = TRACKS.length === 1;      // a single track beds; a list advances on `ended`
    el.preload = 'none';                // 8MB is not something to fetch before it's asked for
    el.crossOrigin = 'anonymous';       // required for createMediaElementSource — see the note above
    el.playsInline = true;
    el.setAttribute('playsinline', '');
    el.setAttribute('aria-hidden', 'true');
    el.style.display = 'none';
    document.body.appendChild(el);      // a detached element won't play in some browsers

    el.addEventListener('timeupdate', function () {
      var now = Date.now();
      if (now - saveT < 900) return;    // ~4 fire per second; we don't need that many writes
      saveT = now;
      writeState();
    });
    el.addEventListener('ended', function () {
      if (TRACKS.length < 2) return;
      trackIdx = (trackIdx + 1) % TRACKS.length;
      pendingSeek = 0;
      loadTrack(0);
      paintUI();
      el.play().catch(function () {});
    });
    el.addEventListener('error', function () {
      playFailed = 'media error ' + (el.error ? el.error.code : '?');
    });
    claimPlayback();
    graph();
    return true;
  }

  function loadTrack(seekTo) {
    var t = TRACKS[trackIdx];
    if (el.getAttribute('data-inno-track') !== t.src) {
      el.setAttribute('data-inno-track', t.src);
      el.preload = 'auto';
      el.src = t.src;
    }
    if (seekTo > 0) {
      // currentTime can only be set once the duration is known
      var seek = function () { try { el.currentTime = Math.min(seekTo, (el.duration || seekTo)); } catch (e) {} };
      if (el.readyState >= 1) seek();
      else el.addEventListener('loadedmetadata', seek, { once: true });
    }
  }

  function level() { return (playing && !ducked) ? VOL : 0; }
  function applyGain(fade) {
    var v = level();
    if (gain && ctx) {
      gain.gain.setTargetAtTime(v, ctx.currentTime, fade == null ? FADE : fade);
      return;
    }
    try { el.volume = v; } catch (e) {}   // no-op on iOS; that's what the graph is for
  }

  function paintUI() {
    for (var i = 0; i < transports.length; i++) {
      var t = transports[i];
      if (t.play) {
        t.play.innerHTML = playing ? '&#10074;&#10074;' : '&#9654;';
        t.play.setAttribute('aria-label', playing ? 'Pause ambience' : 'Play ambience');
      }
      // one track has nothing to skip to — the control would be a lie
      if (t.next) t.next.hidden = TRACKS.length < 2;
      t.root.classList.toggle('playing', playing);
      if (t.name) t.name.textContent = TRACKS[trackIdx].name;
    }
  }

  function start() {
    if (!el && !build()) return false;
    // An explicit press is a request for sound, so it never starts into a duck. The duck is a
    // transition device; a stale one would flip the button to "pause" over a silent output.
    ducked = false;
    claimPlayback();
    clearTimeout(pauseT);
    loadTrack(pendingSeek);
    pendingSeek = 0;
    playing = true;
    writeState();
    paintUI();
    if (ctx && ctx.state === 'suspended') { var r = ctx.resume(); if (r && r.catch) r.catch(function () {}); }
    applyGain();
    var p = el.play();
    if (p && p.catch) p.catch(function (e) { playFailed = String((e && e.name) || e); });
    return !el.paused;
  }
  function stop() {
    playing = false;
    writeState();
    paintUI();
    applyGain();
    // pause only AFTER the fade, or the level drop is a cut rather than a fall
    clearTimeout(pauseT);
    pauseT = setTimeout(function () { if (!playing && el) el.pause(); }, 700);
  }

  // a refused play() rejects asynchronously, so this is armed whenever a session is resumed
  // rather than only when start() reports failure
  function armRevive() {
    var revive = function () {
      document.removeEventListener('pointerdown', revive, true);
      document.removeEventListener('touchend', revive, true);
      document.removeEventListener('keydown', revive, true);
      if (!playing || !el || !el.paused) return;
      if (ctx && ctx.state === 'suspended') { var r = ctx.resume(); if (r && r.catch) r.catch(function () {}); }
      applyGain();
      el.play().catch(function () {});
    };
    document.addEventListener('pointerdown', revive, true);
    document.addEventListener('touchend', revive, true);
    document.addEventListener('keydown', revive, true);
  }

  // ---- the transport UI ----
  // The homepage renders one in its bottom-left dock. Everywhere else it is added as a row
  // inside the hamburger panel: the one place on those pages guaranteed not to collide with the
  // sticky buy bar, the header or the lattice. Audio runs whether or not any UI exists.
  /* The menu copy ships its own styling, because the markup is built here and the rules for it
     were living in inno-site.css — which is loaded ONLY by the homepage template. Everywhere
     else (every product page, which is exactly where the menu copy is the only transport there
     is) the row rendered as bare UA buttons and an unstyled span.

     This is the homepage dock's treatment, line for line: bracketed mono at micro weight, the
     transport at .55 lifting to full on hover and on the playing button, and the four eq bars —
     including the animation, which the old block left out even where it did apply. */
  function css() {
    if (document.getElementById('inno-player-css')) return;
    var s = document.createElement('style');
    s.id = 'inno-player-css';
    s.textContent =
      '.player-in-menu{display:flex;align-items:center;gap:6px;margin-top:18px;' +
        'padding:14px 20px 0;border-top:1px solid rgba(232,228,220,.14);' +
        'font-family:var(--mono,"IBM Plex Mono",ui-monospace,SFMono-Regular,Menlo,monospace);' +
        'font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--bone,#E8E4DC);}' +
      /* 12px, not the row's 10px: the dock bumps its glyphs on a phone so the transport marks read
         at the size of a control rather than of a caption, while the padding stays the hit area. */
      '.player-in-menu .p-btn{background:none;border:0;color:inherit;font:inherit;font-size:12px;' +
        'padding:10px 3px;min-width:32px;min-height:38px;display:inline-flex;align-items:center;' +
        'justify-content:center;opacity:.55;transition:opacity .2s ease;cursor:pointer;}' +
      /* ONE bracket around the whole transport, not a pair per button — that is the dock's own
         grammar, and the block this replaces got it wrong: `[ ▶ ] [ » ] signal 01` instead of
         `[ ▶ » signal 01 ]`. The negative margins are the dock's too; they pull the brackets
         tight against the row rather than letting the flex gap open them up. */
      /* NON-BREAKING spaces, as the dock has them. A plain space in `content` is collapsible,
         so the gap inside the bracket is not guaranteed to survive — measured char 160 on the
         dock against char 32 here, which is a visible difference in how tight the bracket sits. */
      '.player-in-menu::before{content:"[ ";opacity:.55;margin-right:-4px;}' +
      '.player-in-menu::after{content:" ]";opacity:.55;margin-left:-4px;}' +
      '.player-in-menu .p-btn:hover,.player-in-menu .p-btn:focus-visible{opacity:1;}' +
      '.player-in-menu.playing [data-inno-play]{opacity:1;}' +
      '.player-in-menu .p-track{opacity:.55;min-width:0;white-space:nowrap;overflow:hidden;' +
        'text-overflow:ellipsis;}' +
      '.player-in-menu .p-eq{display:flex;gap:2px;align-items:flex-end;height:9px;opacity:.55;' +
        'flex:0 0 auto;}' +
      '.player-in-menu .p-eq i{width:1.5px;height:100%;background:currentColor;' +
        'transform-origin:bottom;transform:scaleY(.15);}' +
      '.player-in-menu.playing .p-eq{opacity:1;}' +
      '.player-in-menu.playing .p-eq i{animation:innoEq .9s ease-in-out infinite;}' +
      '.player-in-menu.playing .p-eq i:nth-child(2){animation-duration:.7s;animation-delay:.1s;}' +
      '.player-in-menu.playing .p-eq i:nth-child(3){animation-duration:1.1s;animation-delay:.2s;}' +
      '.player-in-menu.playing .p-eq i:nth-child(4){animation-duration:.8s;animation-delay:.05s;}' +
      '@keyframes innoEq{0%,100%{transform:scaleY(.2);}50%{transform:scaleY(1);}}' +
      '@media (prefers-reduced-motion:reduce){.player-in-menu.playing .p-eq i{animation:none;}}' +
      /* the panel it belongs to is a phone-only sheet. On a wide screen that same .ph-nav /
         .inno-header-nav IS the inline desktop header, where a transport row would land in the
         middle of the navigation. */
      '@media (min-width:601px){.player-in-menu{display:none;}}';
    (document.head || document.documentElement).appendChild(s);
  }

  function markup(withIds) {
    // ids only on the FIRST transport — the homepage's dock markup already owns #playBtn etc.,
    // and a second element sharing those ids would be a duplicate-id document
    return '<button class="p-btn hot" data-inno-play type="button"' + (withIds ? ' id="playBtn"' : '') + ' aria-label="Play ambience">&#9654;</button>' +
           '<button class="p-btn hot" data-inno-next type="button"' + (withIds ? ' id="nextBtn"' : '') + ' aria-label="Next signal">&#187;</button>' +
           '<span class="p-track" data-inno-track-name' + (withIds ? ' id="trackName"' : '') + '></span>' +
           '<span class="p-eq"><i></i><i></i><i></i><i></i></span>';
  }
  function register(root) {
    if (!root || root._innoWired) return;
    root._innoWired = true;
    var t = {
      root: root,
      play: root.querySelector('[data-inno-play]') || root.querySelector('#playBtn'),
      next: root.querySelector('[data-inno-next]') || root.querySelector('#nextBtn'),
      name: root.querySelector('[data-inno-track-name]') || root.querySelector('#trackName') || root.querySelector('.p-track')
    };
    if (!t.play) return;
    t.play.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();     // inside the menu, don't let it close the panel
      toggles++;
      if (playing) stop(); else start();
    });
    if (t.next) t.next.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      if (TRACKS.length < 2) return;
      trackIdx = (trackIdx + 1) % TRACKS.length;
      pendingSeek = 0;
      writeState();
      if (el) { loadTrack(0); if (playing) el.play().catch(function () {}); }
      paintUI();
    });
    transports.push(t);
  }
  // The dock transport is hidden once the shop overlay is up, so a menu copy goes into the
  // hamburger panel on EVERY page that has one — otherwise the music becomes uncontrollable the
  // moment you leave the homescreen, which is worse than having no player at all.
  function ensureUI() {
    css();
    var dock = document.getElementById('player');
    if (dock) register(dock);
    var panels = document.querySelectorAll('.ph-nav, .inno-header-nav');
    for (var i = 0; i < panels.length; i++) {
      if (panels[i].querySelector('.player-in-menu')) continue;
      var box = document.createElement('div');
      box.className = 'player-in-menu';
      box.innerHTML = markup(!dock && i === 0);
      panels[i].appendChild(box);
      register(box);
    }
    return transports.length > 0;
  }

  function wire() {
    // the SPA builds its headers after this file runs, so try again shortly for the panels
    ensureUI();
    setTimeout(function () { ensureUI(); paintUI(); }, 900);
    setTimeout(function () { ensureUI(); paintUI(); }, 2500);

    var prev = readState();
    if (prev && typeof prev.track === 'number') trackIdx = prev.track % TRACKS.length;
    if (prev && typeof prev.t === 'number') pendingSeek = prev.t;
    paintUI();

    /* ---- tying the sound to the site being open ----

       None of this is automatic. An audio element holding a `playback` audio session is exactly
       what a phone keeps alive in the background, lock-screen transport and all — and that
       session is deliberately claimed, because it is also the only thing that stops the silent
       switch muting the page. So the bed is tied to the document being LOOKED at: hidden pauses
       it, visible picks it straight back up from the same instant.

       Moving between pages is untouched by that. The outgoing document pauses its own audio and
       writes its position on the way out; the incoming one reads that position and carries on.
       What it does rule out is sound continuing after the tab is closed, buried, or swiped away,
       which is the case an <audio> element will happily keep serving on its own. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        writeState();
        if (playing && el && !el.paused) { autoPaused = true; el.pause(); }
      } else if (autoPaused) {
        autoPaused = false;
        if (!playing) return;
        // a page that LOADED hidden has no element yet — it deferred rather than starting sound
        // in a tab nobody is looking at (a middle-clicked product link does exactly that)
        if (!el) { start(); armRevive(); return; }
        if (ctx && ctx.state === 'suspended') { var r = ctx.resume(); if (r && r.catch) r.catch(function () {}); }
        applyGain();
        el.play().catch(function () {});
      }
    });
    window.addEventListener('pagehide', function () {
      // the position at the MOMENT of leaving, not whenever timeupdate last fired — this is what
      // makes the splice on the next page inaudible rather than a small stutter
      writeState();
      if (playing && el && !el.paused) { autoPaused = true; try { el.pause(); } catch (e) {} }
      // hand the media session back, so nothing of ours is left holding the OS transport
      try { if (navigator.audioSession) navigator.audioSession.type = 'auto'; } catch (e) {}
    });
    // restored from bfcache — the same document, still mid-track
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted || !autoPaused) return;
      autoPaused = false;
      if (!playing || !el) return;
      claimPlayback();
      applyGain();
      el.play().catch(function () {});
    });

    if (prev && prev.playing && prev.savedAt && (Date.now() - prev.savedAt) < RESUME_GRACE) {
      if (document.hidden) {
        // opened into a background tab: hold the state, make no sound, and let the visibility
        // handler start it the moment the tab is actually looked at
        playing = true; autoPaused = true; paintUI();
      } else {
        start();
        armRevive();
      }
    } else if (prev && prev.playing) {
      // stale session: keep the position, but the visitor presses play themselves
      playing = false;
      writeState();
      paintUI();
    }
  }

  // duck (don't stop) while a page transition or the cart is over the field
  window.innoDuck = function (on) { ducked = !!on; applyGain(0.3); };
  window.innoPlayer = function () {
    return {
      playing: playing, ducked: ducked, track: trackIdx, name: TRACKS[trackIdx].name,
      paused: el ? el.paused : null,
      at: (el && isFinite(el.currentTime)) ? +el.currentTime.toFixed(1) : null,
      dur: (el && isFinite(el.duration)) ? +el.duration.toFixed(1) : null,
      ready: el ? el.readyState : null,      // 0 nothing … 4 enough buffered to finish
      net: el ? el.networkState : null,      // 2 = still fetching, 3 = found no source
      err: (el && el.error) ? ('code ' + el.error.code) : playFailed,
      route: gain ? 'gain node' : (el ? 'element volume (no volume control on iOS)' : 'none'),
      ctx: ctx ? ctx.state : 'none',
      level: gain ? +gain.gain.value.toFixed(3) : (el ? el.volume : null),
      session: (navigator.audioSession && navigator.audioSession.type) || 'n/a',
      transports: transports.length, toggles: toggles
    };
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
  else wire();
})();
